/**
 * Community Guidelines suspension on the client.
 *
 * - isSafetySuspendedError: any 403 safety_suspended (session API, initiate,
 *   storableError copies).
 * - Removal notice in sessionStorage so "You were removed" survives an iOS
 *   background / pageshow / reload, even if the live session is gone.
 * - useSafetySuspended: GET /api/safety/me once per page load (cached), used
 *   to disable Book / Start now / Book Next Available.
 */
import { useEffect, useState } from 'react';

import { fetchSafetyMe } from './api';

export const SAFETY_SUSPENDED_CODE = 'safety_suspended';
export const SUSPENDED_NOTICE =
  'Your account is suspended for breaking our Community Guidelines. If you think this is a mistake, email info@sweetlisteners.com to dispute it. We reply within one business day.';
export const SUSPENDED_EMAIL = 'info@sweetlisteners.com';
export const REMOVAL_NOTICE_PREFIX = 'sl-removal-notice-v1:';
const POLICY_REASONS = new Set([
  'contact_policy',
  'sexual_policy',
  'conduct_policy',
  'policy_severe',
]);

const codeOf = e =>
  e?.code || e?.data?.code || e?.data?.data?.code || e?.apiErrors?.[0]?.code || null;

/**
 * @param {Object} [e]
 * @returns {boolean}
 */
export const isSafetySuspendedError = e => !!e && codeOf(e) === SAFETY_SUSPENDED_CODE;

/**
 * Removal details carried on a safety_suspended 403, or null.
 *
 * @param {Object} [e]
 * @returns {{ sessionId?: string, endedReason?: string, removedRole?: string, chargeOutcome?: string }|null}
 */
export const safetyRemovalFromError = e => {
  if (!isSafetySuspendedError(e)) {
    return null;
  }
  const details = e?.details || e?.data?.details || e?.data?.data?.details || null;
  return details && typeof details === 'object' ? details : null;
};

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

/**
 * Remember the last guidelines removal for this booking (this tab).
 *
 * @param {string} transactionId
 * @param {{ endedReason: string, removedRole?: string|null, chargeOutcome?: string|null }} notice
 */
export const rememberRemovalNotice = (transactionId, notice) => {
  const store = storage();
  if (!store || !transactionId || !notice || !POLICY_REASONS.has(notice.endedReason)) {
    return;
  }
  try {
    store.setItem(
      `${REMOVAL_NOTICE_PREFIX}${transactionId}`,
      JSON.stringify({
        endedReason: notice.endedReason,
        removedRole: notice.removedRole || null,
        chargeOutcome: notice.chargeOutcome || null,
        at: Date.now(),
      })
    );
  } catch (e) {
    // Private mode quota: the server status still drives the card.
  }
};

/**
 * @param {string} transactionId
 * @returns {{ endedReason: string, removedRole: string|null, chargeOutcome: string|null }|null}
 */
export const readRemovalNotice = transactionId => {
  const store = storage();
  if (!store || !transactionId) {
    return null;
  }
  try {
    const raw = JSON.parse(store.getItem(`${REMOVAL_NOTICE_PREFIX}${transactionId}`) || 'null');
    return raw && POLICY_REASONS.has(raw.endedReason) ? raw : null;
  } catch (e) {
    return null;
  }
};

/**
 * Session-shaped ended payload for a stored / error removal.
 *
 * @param {{ endedReason: string, removedRole?: string|null, chargeOutcome?: string|null }} notice
 * @returns {Object|null}
 */
export const removalSessionPayload = notice =>
  notice && POLICY_REASONS.has(notice.endedReason)
    ? {
        ended: true,
        endedReason: notice.endedReason,
        contactRemovedRole: notice.removedRole || null,
        contactRemovalChargeOutcome: notice.chargeOutcome || null,
      }
    : null;

let cached = null;
let inflight = null;

/**
 * Mark the account suspended (after any safety_suspended 403).
 */
export const markSafetySuspended = () => {
  cached = { ...(cached || {}), suspended: true };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('sl-safety-suspended'));
  }
};

export const resetSafetySuspendedCacheForTests = () => {
  cached = null;
  inflight = null;
};

/**
 * @param {boolean} [enabled] only fetch for logged-in users
 * @returns {boolean} true when the logged-in account is suspended
 */
export const useSafetySuspended = (enabled = true) => {
  const [suspended, setSuspended] = useState(!!cached?.suspended);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }
    let active = true;
    const onMark = () => active && setSuspended(true);
    window.addEventListener('sl-safety-suspended', onMark);
    if (cached) {
      setSuspended(!!cached.suspended);
    } else {
      inflight =
        inflight ||
        fetchSafetyMe().then(data => {
          cached = {
            ...(data || {}),
            suspended: !!data?.suspended || !!cached?.suspended,
          };
          return cached;
        });
      inflight.then(data => active && setSuspended(!!data?.suspended)).catch(() => {});
    }
    return () => {
      active = false;
      window.removeEventListener('sl-safety-suspended', onMark);
    };
  }, [enabled]);
  return suspended;
};
